import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Mail, Send, Paperclip, X, Loader2, Sparkles, FileText, Zap, Eye } from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILES = 3;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

/**
 * ProspectionEmailDialog — send a prospection email from the logged-in Wasabi user
 * with template selection (admin outil / AI), attachments, and full editing.
 * Used as a trigger-wrapper: <ProspectionEmailDialog prospect={...} trigger={<Button/>} />
 */
export default function ProspectionEmailDialog({ prospect, trigger, onSent }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(prospect?.email_professionnel || prospect?.email || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [activeTemplate, setActiveTemplate] = useState('outil');  // outil | ia | perso
  const [emailType, setEmailType] = useState('prospection');  // prospection | documentation
  const [emailTypeManuallySet, setEmailTypeManuallySet] = useState(false);
  const [adminTpl, setAdminTpl] = useState({ subject: '', body: '' });
  const [aiTpl, setAiTpl] = useState({ subject: '', body: '', hook: '' });
  const [myTemplates, setMyTemplates] = useState([]);
  const [myTplId, setMyTplId] = useState('');
  const [loadingAi, setLoadingAi] = useState(false);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  // Load admin-saved template + personal templates when dialog opens
  useEffect(() => {
    if (!open) return;
    setRecipient(prospect?.email_professionnel || prospect?.email || '');
    (async () => {
      try {
        const [{ data: admin }, { data: mine }] = await Promise.all([
          api.get('/maps/systeme-u/mail-template'),
          api.get('/users/me/email-templates'),
        ]);
        setAdminTpl({ subject: admin.subject || '', body: admin.body || '' });
        const tpls = mine.templates || [];
        setMyTemplates(tpls);
        const defaultTpl = tpls.find(t => t.is_default);
        if (defaultTpl) {
          setActiveTemplate('perso');
          setMyTplId(defaultTpl.id);
          setSubject(defaultTpl.subject || '');
          setBody(defaultTpl.body || '');
        } else {
          setActiveTemplate('outil');
          setSubject(admin.subject || '');
          setBody(admin.body || '');
        }
      } catch (err) {
        toast.error('Impossible de charger les templates');
      }
    })();
  }, [open, prospect?.id]);

  const generateAi = async () => {
    setLoadingAi(true);
    try {
      const { data } = await api.post('/maps/systeme-u/generate-ai-template', {
        style: 'professionnel-chaleureux',
        longueur: 'moyen',
      });
      setAiTpl({ subject: data.subject, body: data.body, hook: data.hook });
      setActiveTemplate('ia');
      setSubject(data.subject);
      setBody(data.body);
      toast.success(`Template IA chargé · ${data.hook}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération IA');
    } finally {
      setLoadingAi(false);
    }
  };

  const switchTemplate = (which, personalId = null) => {
    setActiveTemplate(which);
    if (which === 'outil') {
      setSubject(adminTpl.subject);
      setBody(adminTpl.body);
    } else if (which === 'ia' && aiTpl.subject) {
      setSubject(aiTpl.subject);
      setBody(aiTpl.body);
    } else if (which === 'perso') {
      const tpl = myTemplates.find(t => t.id === personalId) || myTemplates.find(t => t.id === myTplId) || myTemplates[0];
      if (tpl) {
        setMyTplId(tpl.id);
        setSubject(tpl.subject || '');
        setBody(tpl.body || '');
      }
    }
  };

  const onFilesAdded = (e) => {
    const newOnes = Array.from(e.target.files || []);
    const merged = [...files, ...newOnes].slice(0, MAX_FILES);
    const total = merged.reduce((s, f) => s + f.size, 0);
    if (total > MAX_SIZE) {
      toast.error(`Taille totale dépasse ${MAX_SIZE / 1024 / 1024} Mo`);
      return;
    }
    setFiles(merged);
    e.target.value = '';
    // Auto-suggest "documentation" when user adds attachments (unless they already chose manually)
    if (merged.length > 0 && !emailTypeManuallySet && emailType !== 'documentation') {
      setEmailType('documentation');
      toast.info('Type basculé sur "Documentation" (PJ détectée)', { duration: 3000 });
    }
  };

  const removeFile = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    // If no PJ remains and user didn't manually choose, revert to prospection
    if (next.length === 0 && !emailTypeManuallySet) setEmailType('prospection');
  };

  const setEmailTypeManual = (val) => {
    setEmailType(val);
    setEmailTypeManuallySet(true);
  };

  const doPreview = () => {
    // Local render of {variables} for preview
    const vars = {
      enseigne: prospect?.enseigne || '',
      ville: prospect?.ville || '',
      code_postal: prospect?.code_postal || '',
      commune: prospect?.ville || '',
      rue: prospect?.rue || '',
      prenom_dirigeant: prospect?.prenom_dirigeant || '',
      nom_dirigeant: prospect?.nom_dirigeant || '',
      dirigeant: `${prospect?.prenom_dirigeant || ''} ${prospect?.nom_dirigeant || ''}`.trim(),
      nom_magasin: prospect?.nom_magasin || '',
    };
    const render = (t) => (t || '').replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
    setPreview({ subject: render(subject), body: render(body) });
  };

  const send = async () => {
    if (!recipient || !subject.trim() || !body.trim()) {
      toast.error('Destinataire, objet et corps obligatoires');
      return;
    }
    setSending(true);
    try {
      const form = new FormData();
      form.append('recipient_email', recipient.trim());
      form.append('subject', subject);
      form.append('body', body);
      form.append('template_used', activeTemplate);
      form.append('email_type', emailType);
      files.forEach(f => form.append('files', f));
      const { data } = await api.post(`/prospects/${prospect.id}/send-email`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const typeLabel = emailType === 'documentation' ? '📎 Documentation' : '📧 Prospection';
      let msg = `${typeLabel} envoyée à ${recipient} (BCC vous)${files.length ? ` + ${files.length} PJ` : ''}`;
      if (data.new_statut_commercial) {
        msg += ` · statut → ${data.new_statut_commercial === 'mail_doc_envoye' ? 'Mail Doc envoyé' : 'Mail Prospection envoyé'}`;
      } else if (data.statut_protected) {
        msg += ' · statut conservé (RDV/négo/converti)';
      }
      toast.success(msg, { duration: 6000 });
      setOpen(false);
      setFiles([]);
      setPreview(null);
      onSent?.(data.email);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi email');
    } finally {
      setSending(false);
    }
  };

  const totalSize = files.reduce((s, f) => s + f.size, 0);
  const sizeStr = totalSize > 1024 * 1024 ? `${(totalSize / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(totalSize / 1024)} Ko`;

  return (
    <>
      {React.cloneElement(trigger, { onClick: () => setOpen(true) })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="prospection-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#047857]" /> Envoyer un email de prospection
            </DialogTitle>
            <DialogDescription>
              À {prospect?.enseigne} {prospect?.ville} · Dirigeant : {prospect?.prenom_dirigeant} {prospect?.nom_dirigeant}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Destinataire */}
            <div>
              <Label className="text-xs font-semibold text-[#334155]">Destinataire</Label>
              <Input value={recipient} onChange={e => setRecipient(e.target.value)}
                placeholder="prospect@example.com"
                data-testid="email-recipient-input"
                className="mt-1"/>
              {!prospect?.email_professionnel && !prospect?.email && (
                <div className="text-[11px] text-[#C2410C] mt-0.5">⚠️ Pas d'email en base — à renseigner manuellement</div>
              )}
            </div>

            {/* Type d'envoi — bascule auto le statut du prospect */}
            <div className="bg-[#FAFAFA] rounded-lg p-3 border border-[#E2E8F0]">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <Label className="text-xs font-semibold text-[#334155]">Type d'envoi</Label>
                  <p className="text-[11px] text-[#71717A] mt-0.5">
                    Bascule auto le statut → "Mail Prospection envoyé" ou "Mail Doc envoyé"
                    {emailTypeManuallySet ? '' : ' · Auto-détection si pièce jointe'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant={emailType === 'prospection' ? 'default' : 'outline'}
                    onClick={() => setEmailTypeManual('prospection')}
                    className={`h-8 text-xs ${emailType === 'prospection' ? 'bg-[#0055FF] hover:bg-[#0040CC] text-white' : ''}`}
                    data-testid="email-type-prospection-btn">
                    📧 Prospection
                  </Button>
                  <Button size="sm" variant={emailType === 'documentation' ? 'default' : 'outline'}
                    onClick={() => setEmailTypeManual('documentation')}
                    className={`h-8 text-xs ${emailType === 'documentation' ? 'bg-[#8B5CF6] hover:bg-[#7C3AED] text-white' : ''}`}
                    data-testid="email-type-documentation-btn">
                    📎 Documentation
                  </Button>
                </div>
              </div>
            </div>

            {/* Template selector */}
            <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E2E8F0] space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-[#334155] uppercase tracking-wide">Template</div>
                <Button size="sm" variant="outline" onClick={generateAi} disabled={loadingAi}
                  className="h-7 text-xs border-[#047857]/40 text-[#047857] hover:bg-[#ECFDF5]"
                  data-testid="email-generate-ai-btn">
                  {loadingAi ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <Zap className="w-3 h-3 mr-1"/>}
                  Générer une version IA
                </Button>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant={activeTemplate === 'outil' ? 'default' : 'outline'}
                  onClick={() => switchTemplate('outil')}
                  className={`flex-1 h-8 text-xs ${activeTemplate === 'outil' ? 'bg-[#334155] hover:bg-[#1E293B] text-white' : ''}`}
                  data-testid="email-tpl-outil-btn">
                  📝 Outil (admin)
                </Button>
                <Button size="sm" variant={activeTemplate === 'ia' ? 'default' : 'outline'}
                  onClick={() => switchTemplate('ia')} disabled={!aiTpl.subject}
                  className={`flex-1 h-8 text-xs ${activeTemplate === 'ia' ? 'bg-[#047857] hover:bg-[#065F46] text-white' : ''}`}
                  data-testid="email-tpl-ia-btn">
                  ✨ IA {aiTpl.subject ? '' : '(à générer)'}
                </Button>
                <Button size="sm" variant={activeTemplate === 'perso' ? 'default' : 'outline'}
                  onClick={() => switchTemplate('perso')} disabled={myTemplates.length === 0}
                  className={`flex-1 h-8 text-xs ${activeTemplate === 'perso' ? 'bg-[#F97316] hover:bg-[#EA580C] text-white' : ''}`}
                  data-testid="email-tpl-perso-btn">
                  🖋 Mes templates {myTemplates.length === 0 ? '(aucun)' : `(${myTemplates.length})`}
                </Button>
              </div>
              {activeTemplate === 'perso' && myTemplates.length > 0 && (
                <select
                  value={myTplId}
                  onChange={e => switchTemplate('perso', e.target.value)}
                  className="w-full h-8 text-xs bg-white border border-[#E5E7EB] rounded px-2"
                  data-testid="email-tpl-perso-select"
                >
                  {myTemplates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' ★' : ''}</option>
                  ))}
                </select>
              )}
              {activeTemplate === 'ia' && aiTpl.hook && (
                <div className="text-[11px] italic text-[#065F46] bg-white/70 px-2 py-1 rounded">💡 {aiTpl.hook}</div>
              )}
            </div>

            {/* Subject */}
            <div>
              <Label className="text-xs font-semibold text-[#334155]">Objet</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)}
                className="mt-1"
                data-testid="email-subject-input"/>
            </div>

            {/* Body */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold text-[#334155]">Corps du mail</Label>
                <Button size="sm" variant="ghost" onClick={doPreview}
                  className="h-6 text-[11px] text-[#64748B]"
                  data-testid="email-preview-btn">
                  <Eye className="w-3 h-3 mr-1"/> Prévisualiser
                </Button>
              </div>
              <Textarea value={body} onChange={e => setBody(e.target.value)} rows={12}
                className="font-mono text-[13px] leading-relaxed"
                data-testid="email-body-textarea"/>
              <div className="text-[10px] text-[#64748B] mt-0.5">
                {body.length} car. · Variables actives : <code>{`{prenom_dirigeant} {nom_dirigeant} {enseigne} {ville} {code_postal}`}</code>
              </div>
            </div>

            {/* Preview box */}
            {preview && (
              <div className="bg-white border-2 border-[#047857]/30 rounded-lg p-3 space-y-2" data-testid="email-preview-box">
                <div className="text-[11px] text-[#71717A] uppercase font-semibold">Aperçu envoyé</div>
                <div className="border-l-4 border-[#047857] pl-3 py-1 bg-[#F0FDF4]">
                  <div className="text-[10px] text-[#71717A]">Objet</div>
                  <div className="text-sm font-medium">{preview.subject}</div>
                </div>
                <div className="border-l-4 border-[#047857] pl-3 py-1 bg-[#F0FDF4]">
                  <div className="text-[10px] text-[#71717A]">Corps</div>
                  <pre className="text-[13px] whitespace-pre-wrap font-sans leading-relaxed">{preview.body}</pre>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setPreview(null)}
                  className="h-6 text-[11px] text-[#64748B]">Fermer l'aperçu</Button>
              </div>
            )}

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold text-[#334155]">
                  Pièces jointes ({files.length}/{MAX_FILES} · {sizeStr}/10 Mo)
                </Label>
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
                  disabled={files.length >= MAX_FILES}
                  className="h-7 text-xs"
                  data-testid="email-add-file-btn">
                  <Paperclip className="w-3 h-3 mr-1"/> Ajouter un fichier
                </Button>
                <input type="file" ref={fileRef} className="hidden" multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.doc,.xlsx"
                  onChange={onFilesAdded}
                  data-testid="email-file-input"/>
              </div>
              {files.length > 0 && (
                <div className="space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#F1F5F9] rounded px-2 py-1 text-xs" data-testid={`email-attachment-${i}`}>
                      <FileText className="w-3.5 h-3.5 text-[#334155]"/>
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-[10px] text-[#64748B]">{Math.round(f.size / 1024)} Ko</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-[#C2410C] hover:text-[#991B1B]"
                        data-testid={`email-remove-file-${i}`}>
                        <X className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="bg-[#DBEAFE] border border-[#93C5FD] rounded-lg p-2.5 text-[11px] text-[#1E3A8A]">
              📧 Envoyé depuis votre adresse Wasabi · BCC automatique vers vous · Reply-To = votre adresse · pastille "Email envoyé" ajoutée sur la ligne du prospect.
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="email-cancel-btn">
              Annuler
            </Button>
            <Button onClick={send} disabled={sending || !recipient || !subject.trim() || !body.trim()}
              className="bg-[#047857] hover:bg-[#065F46] text-white"
              data-testid="email-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Send className="w-4 h-4 mr-1.5"/>}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * EmailHistoryBadge — pill/badge to show email sent count on prospect rows.
 * Click opens a dialog listing all sent emails with date/time + body.
 */
export function EmailHistoryBadge({ prospectId, count = 0 }) {
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/prospects/${prospectId}/emails`);
      setEmails(data.emails || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  if (!count) return null;

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] border border-[#6EE7B7] text-[11px] text-[#065F46] font-medium hover:bg-[#D1FAE5] transition-colors"
        data-testid={`email-badge-${prospectId}`}
        title={`${count} email${count > 1 ? 's' : ''} envoyé${count > 1 ? 's' : ''}`}>
        <Mail className="w-3 h-3"/>
        {count > 1 ? `${count} emails` : 'Email envoyé'}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`email-history-${prospectId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#047857]"/> Historique des emails
            </DialogTitle>
            <DialogDescription>{count} email{count > 1 ? 's' : ''} envoyé{count > 1 ? 's' : ''} pour ce prospect</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#047857]"/></div>
          ) : (
            <div className="space-y-3">
              {emails.map((e, i) => (
                <div key={e.id || i} className="bg-white rounded-lg border border-[#E2E8F0] p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{e.subject}</div>
                      <div className="text-[11px] text-[#64748B] mt-0.5">
                        {fmt(e.sent_at)} · De : <strong>{e.sender_email}</strong> · À : <strong>{e.recipient_email}</strong>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-[#047857]/40 text-[#047857] bg-[#ECFDF5]">
                      {e.template_used === 'ia' ? '✨ IA' : e.template_used === 'outil' ? '📝 Outil' : e.template_used === 'perso' ? '🖋 Perso' : '🖋 Custom'}
                    </Badge>
                  </div>
                  <div className="bg-[#F8FAFC] rounded p-3 border-l-4 border-[#047857] whitespace-pre-wrap text-[13px] text-[#334155] leading-relaxed">{e.body}</div>
                  {e.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {e.attachments.map((a, j) => (
                        <Badge key={j} variant="outline" className="text-[10px]">
                          <FileText className="w-3 h-3 mr-1"/> {a.filename} ({Math.round((a.size || 0) / 1024)} Ko)
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {!emails.length && <div className="text-center text-sm text-[#64748B] py-8">Aucun email trouvé</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
